import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useOpen } from 'hooks/use-open';

import { buildNonModeledRows } from '../helpers/build-non-modeled-rows';
import { buildOverviewRows } from '../helpers/build-overview-rows';
import { formatMetricValue } from '../helpers/results-format';
import {
  DEFAULT_RESULTS_TAB,
  TOTAL_FIRST_YEAR_CARDS,
} from '../results-step.constants';

import {
  useAidResults,
  useAidResultsCoa,
  useDownloadAidResultsDataset,
} from '../../../hooks/use-aid-results';
import { useAidModel } from '../../../hooks/use-aid-model-details';
import { useCompareEstimates } from './use-compare-estimates';

import { useCurrentUser } from 'hooks/api/use-current-user';

import type {
  AidModelCoaDto,
  AidModelDto,
  AidModelResultRowDto,
} from 'api/generated/schemas';
import type {
  MetricCardData,
  NonModeledRow,
  OverviewRow,
  ResultsTabValue,
} from '../results-step.types';
import type { UseCompareEstimatesResult } from './use-compare-estimates';

interface UseResultsStepParams {
  clientId?: string | number;
}

interface UseResultsStepResult {
  modelId: string | undefined;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  errorMessage: string;
  hasData: boolean;
  activeTab: ResultsTabValue;
  setActiveTab: (tab: ResultsTabValue) => void;
  metricCards: MetricCardData[];
  overviewRows: OverviewRow[];
  nonModeledRows: NonModeledRow[];
  showModeledActualYear: boolean;
  setShowModeledActualYear: (value: boolean) => void;
  showLastYear: boolean;
  setShowLastYear: (value: boolean) => void;
  isCoaOpen: boolean;
  openCoa: () => void;
  closeCoa: () => void;
  coaGroups: AidModelCoaDto[];
  isCoaLoading: boolean;
  entryTerm: number | undefined;
  actualYearLabel: string;
  compareEstimates: UseCompareEstimatesResult;
  downloadDataset: () => void;
  isDownloadingDataset: boolean;
  canDownloadDataset: boolean;
}

const readCardValue = (
  row: AidModelResultRowDto | undefined,
  field: keyof AidModelResultRowDto | null
): number | null => {
  const raw = field ? row?.[field] : null;

  return typeof raw === 'number' ? raw : null;
};

const buildMetricCards = (
  estimatedTotalFirstYear: AidModelResultRowDto | undefined,
  actualTotalFirstYear: AidModelResultRowDto | undefined
): MetricCardData[] =>
  TOTAL_FIRST_YEAR_CARDS.map((card) => ({
    key: card.key,
    label: card.label,
    value: formatMetricValue(
      readCardValue(estimatedTotalFirstYear, card.field),
      card.kind
    ),
    actualValue: formatMetricValue(
      readCardValue(actualTotalFirstYear, card.field),
      card.kind
    ),
  }));

const buildActualYearLabel = (entryTerm: number | undefined): string =>
  entryTerm ? `Actual ${entryTerm}` : 'Actual';

export const useResultsStep = ({
  clientId: clientIdProp,
}: UseResultsStepParams = {}): UseResultsStepResult => {
  const { clientId: clientIdParam, modelId } = useParams();
  const clientId = clientIdProp ?? clientIdParam ?? '';
  const { data: currentUser } = useCurrentUser();
  const userId = currentUser?.oktaUserId ?? '';
  // Mirrors AidDatasetDownloadController's @Secured roles (SUPER_ADMIN, FD_PRINCIPAL,
  // FD_ANALYST, REPORT_ADMIN) — client-facing USER/INTERNAL_USER roles must not see the button.
  const canDownloadDataset = !!(
    currentUser?.superAdmin ||
    currentUser?.fdPrincipal ||
    currentUser?.fdAnalyst ||
    currentUser?.reportAdmin
  );

  const [activeTab, setActiveTab] =
    useState<ResultsTabValue>(DEFAULT_RESULTS_TAB);
  const [showModeledActualYear, setShowModeledActualYear] = useState(false);
  const [showLastYear, setShowLastYear] = useState(false);
  const {
    isOpen: isCoaOpen,
    handleOpen: openCoa,
    handleClose: closeCoa,
  } = useOpen();

  const resultsQuery = useAidResults({
    userId,
    modelId: modelId ?? '',
    enabled: !!modelId,
  });

  const coaQuery = useAidResultsCoa({
    userId,
    modelId: modelId ?? '',
    enabled: !!modelId && isCoaOpen,
  });

  const downloadDatasetMutation = useDownloadAidResultsDataset();
  const downloadDataset = () => {
    if (!userId || !modelId) return;

    downloadDatasetMutation.mutate({ userId, modelId });
  };

  const modelQuery = useAidModel({
    userId,
    clientId,
    modelId: modelId ?? '',
    enabled: !!modelId && !!clientId,
  });

  // TODO(ts-migration): useAidModel returns a loosely-typed response; narrow when the hook is properly typed
  const modelData = modelQuery.data as AidModelDto | undefined;

  const compareEstimates = useCompareEstimates({
    userId,
    clientId,
    modelId,
    currentModelName: modelData?.name,
  });

  const actualResults = resultsQuery.data?.actualResults;
  const estimatedResults = resultsQuery.data?.estimatedResults;

  const metricCards = useMemo(
    () =>
      buildMetricCards(
        estimatedResults?.totalFirstYear,
        actualResults?.totalFirstYear
      ),
    [estimatedResults, actualResults]
  );

  const overviewRows = useMemo(
    () => buildOverviewRows(actualResults, estimatedResults),
    [actualResults, estimatedResults]
  );

  const nonModeledRows = useMemo(
    () => buildNonModeledRows(estimatedResults, actualResults, showLastYear),
    [estimatedResults, actualResults, showLastYear]
  );

  return {
    modelId,
    isLoading: resultsQuery.isLoading,
    isFetching: resultsQuery.isFetching,
    isError: resultsQuery.isError,
    errorMessage:
      (resultsQuery.error as { data?: { detail?: string } })?.data?.detail ||
      'Failed to load results for this model.',
    hasData: !!resultsQuery.data,
    activeTab,
    setActiveTab,
    metricCards,
    overviewRows,
    nonModeledRows,
    showModeledActualYear,
    setShowModeledActualYear,
    showLastYear,
    setShowLastYear,
    isCoaOpen,
    openCoa,
    closeCoa,
    coaGroups: coaQuery.data ?? [],
    isCoaLoading: coaQuery.isLoading,
    entryTerm: modelData?.entryTerm,
    actualYearLabel: buildActualYearLabel(modelData?.entryTerm),
    compareEstimates,
    downloadDataset,
    isDownloadingDataset: downloadDatasetMutation.isPending,
    canDownloadDataset,
  };
};
